import ScrollModal from "@/components/common/scroll-modal";
import {
  Heading,
  Button,
  Icon,
  GroupTextFieldRow,
  GroupTextField,
  Input,
  useToast,
  Avatar,
  AvatarFallback,
} from "@elaniintc/goes-design-system";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextAreaField } from "@/components/common/text-field";
import { newTableSchema, type TableData } from "@/schemas/data-center";
import { useEffect, useState } from "react";
import {
  useRemoveTableById,
  useUpdateTable,
} from "@/queries/data-center/data-center.api";
import DeactivateTableModal from "./deactivate-modal";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { OptionTooltip } from "@/workflow/components/configurations/form/components/OptionTooltip";

interface TableDataProps {
  table_display_name?: string;
  description: string;
  name: string;
  id?: string | undefined;
}

interface EditTableModal {
  tableData: TableDataProps;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onDeactivate?: () => void;
}

export default function EditTableModal({
  tableData,
  open,
  onClose,
  onSuccess,
  onDeactivate,
}: EditTableModal) {
  const { hasPermission } = useAuthorization();
  const canDeactivateTable = hasPermission(Permissions["data-center"].delete);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(newTableSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      table_display_name: tableData?.table_display_name || "",
      description: tableData?.description || "",
      name: tableData?.name || "",
      id: tableData?.id || undefined,
    },
  });
  const [showDeactivateMessage, setShowDeactivateMessage] = useState(false);
  const [locked, setLocked] = useState(true);
  const { showToast } = useToast();
  const { mutateAsync, isPending: isLoadingUpdateTable } = useUpdateTable();
  const { mutateAsync: deactivateTable, isPending: isLoadingDeactivation } =
    useRemoveTableById();

  useEffect(() => {
    reset({
      id: tableData?.id || "",
      name: tableData?.name || "",
      description: tableData?.description || "",
      table_display_name: tableData?.table_display_name || "",
    });
  }, [open, tableData, reset]);

  const onSubmit = async (data: TableData) => {
    try {
      await mutateAsync({
        id: data?.id as string,
        name: data.name,
        description: data.description,
        table_display_name: data.table_display_name,
      });

      showToast({
        title: "Cambios guardados",
        description:
          "Los cambios realizados en la tabla se guardaron correctamente.",
        options: {
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square">
                <Icon type="far" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
      clearErrors();
      handleClose();
      onSuccess();
    } catch (e) {
      const error = e as AxiosError<{
        message?: string;
        errors?: { [key: string]: string[] };
      }>;
      setError("name", {
        type: "server",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Error al crear la tabla",
      });
      showToast({
        title: "Error al guardar",
        description:
          "Los cambios no pudieron ser guardados. Por favor vuelve a ingresar a la tabla e inténtalo de nuevo.",
        options: {
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="danger" shape="square">
                <Icon type="far" name="circle-exclamation" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    }
  };

  const handleClose = () => {
    reset({
      name: "",
      description: "",
    });
    onClose();
  };

  const handleLockToggle = () => {
    setLocked((l) => !l);
  };

  const nameValue = watch("table_display_name");

  useEffect(() => {
    if (nameValue && locked) {
      const generatedKey = nameValue
        .toLocaleLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "_");

      setValue("name", generatedKey, { shouldValidate: true });
    }
  }, [nameValue, setValue, locked]);

  return (
    <>
      <ScrollModal
        isOpen={open}
        onClose={handleClose}
        modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
        header={
          <div className="flex items-center justify-between px-8 py-3">
            <DialogTitle className="sr-only">Editar Tabla</DialogTitle>
            <DialogDescription className="sr-only">
              Formulario para editar una tabla de datos
            </DialogDescription>
            <Heading variant="h5" className="mb-0!">
              Editar Tabla
            </Heading>
            <Button
              type="button"
              onClick={handleClose}
              variant="quiet"
              aria-label="Cerrar"
              className="w-8 h-8 cursor-pointer"
            >
              <Icon type="far" name="times" />
            </Button>
          </div>
        }
        footer={
          <div className="w-full flex py-3 px-8 gap-3">
            {canDeactivateTable && (
              <Button
                variant="danger-tertiary"
                size="lg"
                type="button"
                className="cursor-pointer"
                onClick={() => setShowDeactivateMessage(true)}
              >
                Desactivar
              </Button>
            )}
            <div className="flex-1 flex justify-end gap-2">
              <Button
                variant="tertiary"
                size="lg"
                type="button"
                onClick={handleClose}
                className="cursor-pointer"
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                size="lg"
                type="submit"
                disabled={!isValid || isLoadingUpdateTable}
                form="update-table-form"
                className="cursor-pointer"
              >
                Guardar Cambios
                {isLoadingUpdateTable && (
                  <Icon
                    type="fas"
                    name="spinner-third"
                    className="animate-spin"
                  />
                )}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="update-table-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
        >
          {/* Contenido scrollable */}
          <div className="flex flex-col gap-6 pb-6">
            {/* Detalles */}
            <div>
              <Heading variant="h6" className="mb-4">
                Detalles
              </Heading>
              <div className="flex flex-col gap-4">
                <GroupTextField
                  label="Nombre de la tabla"
                  required
                  error={
                    errors.table_display_name?.message ||
                    errors.name?.message ||
                    ""
                  }
                >
                  <GroupTextFieldRow>
                    <Input
                      className="w-full"
                      required
                      maxLength={100}
                      placeholder="Escribe un nombre para la tabla"
                      {...register("table_display_name")}
                    />
                  </GroupTextFieldRow>
                  <GroupTextFieldRow>
                    <div className="flex items-center w-full relative">
                      <Input
                        className="w-full pr-12"
                        placeholder="key"
                        disabled={locked}
                        group="down-left"
                        {...register("name")}
                      />
                      <OptionTooltip title="La key es el identificador técnico del campo. Se recomienda no modificarla si ya se ha utilizado en integraciones o automatizaciones.">
                        <div className="absolute right-4 top-3 text-body">
                          <Icon name="circle-info" type="far" />
                        </div>
                      </OptionTooltip>
                    </div>

                    <Button
                      variant="quiet"
                      type="button"
                      onClick={handleLockToggle}
                      aria-label={
                        locked
                          ? "Desbloquear edición de key"
                          : "Bloquear edición de key"
                      }
                    >
                      <Icon name={locked ? "lock" : "lock-open"} type="far" />
                    </Button>
                  </GroupTextFieldRow>
                </GroupTextField>

                <TextAreaField
                  label="Descripción"
                  helperText="Opcional"
                  maxLength={255}
                  placeholder="Describe el contenido de tu tabla"
                  rows={3}
                  className="resize-none w-full"
                  error={errors.description?.message || ""}
                  {...register("description")}
                />
              </div>
            </div>
          </div>
        </form>
      </ScrollModal>
      {showDeactivateMessage && (
        <DeactivateTableModal
          isOpen={showDeactivateMessage}
          isLoading={isLoadingDeactivation}
          onClose={() => {
            setShowDeactivateMessage(false);
          }}
          onAccept={async ({ reason }) => {
            try {
              await deactivateTable({
                id: tableData.id as string,
                reason: reason || "",
              });
              showToast({
                title: "Tabla desactivada",
                description:
                  "La tabla ha sido desactivada correctamente. Puedes volver a activarla desde su configuración.",
                options: {
                  duration: 5000,
                  type: "error",
                },
              });
              setShowDeactivateMessage(false);
              onDeactivate?.();
              handleClose();
            } catch (e) {
              console.error("Error al desactivar la tabla:", e);
              showToast({
                title: "Error al desactivar la tabla",
                description:
                  "Ha ocurrido un error al intentar desactivar la tabla. Por favor, inténtalo de nuevo.",
                options: {
                  duration: 5000,
                  type: "error",
                },
              });
            }
          }}
        />
      )}
    </>
  );
}
